import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import GoBackButton from '../components/GoBackButton';
import { colors } from '../components/Theme/WebTheme';
import { FONT_FAMILY, SECONDORY_COLOR } from '../common/sharedFunctions';

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: '19px',
    backgroundColor: colors.WHITE,
    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontFamily: FONT_FAMILY,
    color: colors.BLACK,
  },
  vehicleAccordion: {
    marginBottom: theme.spacing(2),
    '&:before': {
      display: 'none',
    },
  },
  vehicleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  vehicleImage: {
    width: 50,
    height: 50,
    objectFit: 'cover',
    borderRadius: '8px',
  },
  vehiclePricingGrid: {
    marginTop: '16px',
  },
  resetButton: {
    marginLeft: '8px',
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  originalPrice: {
    textDecoration: 'line-through',
    color: colors.GRAY,
    fontSize: '0.875rem',
  },
  newPrice: {
    color: colors.BLACK,
    fontWeight: 'bold',
  },
  discountChip: {
    backgroundColor: '#4caf50',
    color: 'white',
    fontSize: '0.75rem',
  },
  markupChip: {
    backgroundColor: '#ff9800',
    color: 'white',
    fontSize: '0.75rem',
  },
  saveButton: {
    backgroundColor: colors.MAIN_COLOR,
    color: 'white',
    '&:hover': {
      backgroundColor: colors.MAIN_COLOR_DARK,
    },
  },
  previewTable: {
    marginTop: '24px',
  },
  tableCell: {
    fontWeight: 'bold',
  },
}));

const DriverCustomPricingManagement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const { i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    users,
    error: usersError,
    loading: usersLoading,
  } = useSelector(state => state.usersdata);
  const cartypes = useSelector(state => state.cartypes);

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  const [pricingConfig, setPricingConfig] = useState({
    enabled: false,
    validUntil: '',
    notes: '',
    vehiclePricing: {},
  });

  // Calculate pricing preview for a vehicle type
  const calculatePricingPreview = carType => {
    const vehicleConfig = pricingConfig.vehiclePricing[carType.id];

    if (!pricingConfig.enabled || !vehicleConfig?.enabled) {
      return {
        original: {
          ratePerKm: parseFloat(carType.rate_per_unit_distance),
          baseFare: parseFloat(carType.base_fare),
          minFare: parseFloat(carType.min_fare),
          convenienceFees: parseFloat(carType.convenience_fees),
          fleetAdminFee: parseFloat(carType.fleet_admin_fee),
        },
        adjusted: {
          ratePerKm: parseFloat(carType.rate_per_unit_distance),
          baseFare: parseFloat(carType.base_fare),
          minFare: parseFloat(carType.min_fare),
          convenienceFees: parseFloat(carType.convenience_fees),
          fleetAdminFee: parseFloat(carType.fleet_admin_fee),
        },
        changes: {
          ratePerKm: { discount: 0, isDiscount: false },
          baseFare: { discount: 0, isDiscount: false },
          minFare: { discount: 0, isDiscount: false },
          convenienceFees: { discount: 0, isDiscount: false },
          fleetAdminFee: { discount: 0, isDiscount: false },
        },
        hasCustomPricing: false,
      };
    }

    const original = {
      ratePerKm: parseFloat(carType.rate_per_unit_distance),
      baseFare: parseFloat(carType.base_fare),
      minFare: parseFloat(carType.min_fare),
      convenienceFees: parseFloat(carType.convenience_fees),
      fleetAdminFee: parseFloat(carType.fleet_admin_fee),
    };

    const adjusted = {
      ratePerKm: vehicleConfig.ratePerKm,
      baseFare: vehicleConfig.baseFare,
      minFare: vehicleConfig.minFare,
      convenienceFees: vehicleConfig.convenienceFees,
      fleetAdminFee: vehicleConfig.fleetAdminFee,
    };

    const changes = {
      ratePerKm: {
        discount: Math.abs(
          ((original.ratePerKm - adjusted.ratePerKm) / original.ratePerKm) * 100
        ),
        isDiscount: adjusted.ratePerKm < original.ratePerKm,
      },
      baseFare: {
        discount: Math.abs(
          ((original.baseFare - adjusted.baseFare) / original.baseFare) * 100
        ),
        isDiscount: adjusted.baseFare < original.baseFare,
      },
      minFare: {
        discount: Math.abs(
          ((original.minFare - adjusted.minFare) / original.minFare) * 100
        ),
        isDiscount: adjusted.minFare < original.minFare,
      },
      convenienceFees: {
        discount: Math.abs(
          ((original.convenienceFees - adjusted.convenienceFees) /
            original.convenienceFees) *
            100
        ),
        isDiscount: adjusted.convenienceFees < original.convenienceFees,
      },
      fleetAdminFee: {
        discount: Math.abs(
          ((original.fleetAdminFee - adjusted.fleetAdminFee) /
            original.fleetAdminFee) *
            100
        ),
        isDiscount: adjusted.fleetAdminFee < original.fleetAdminFee,
      },
    };

    return { original, adjusted, changes, hasCustomPricing: true };
  };

  // Render price cell with before/after comparison
  const renderPriceCell = (
    original,
    adjusted,
    change,
    prefix = '',
    suffix = '',
    hasCustomPricing = false
  ) => {
    if (!hasCustomPricing || original === adjusted) {
      return (
        <span>
          {prefix}
          {original.toFixed(2)}
          {suffix}
        </span>
      );
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <span className={classes.originalPrice}>
          {prefix}
          {original.toFixed(2)}
          {suffix}
        </span>
        <span className={classes.newPrice}>
          {prefix}
          {adjusted.toFixed(2)}
          {suffix}
        </span>
        {change.discount > 0 && (
          <Chip
            label={`${change.discount.toFixed(1)}% ${change.isDiscount ? 'off' : 'markup'}`}
            size="small"
            className={
              change.isDiscount ? classes.discountChip : classes.markupChip
            }
          />
        )}
      </Box>
    );
  };

  // Initialize vehicle pricing from car types
  useEffect(() => {
    if (cartypes && cartypes.cars) {
      const vehiclePricing = {};
      cartypes.cars.forEach(carType => {
        vehiclePricing[carType.id] = {
          enabled: false,
          convenienceFees: parseFloat(carType.convenience_fees),
          convenienceFeeType: carType.convenience_fee_type || 'percentage',
          fleetAdminFee: parseFloat(carType.fleet_admin_fee),
        };
      });
      setPricingConfig(prev => ({
        ...prev,
        vehiclePricing,
      }));
    }
  }, [cartypes]);

  // Find driver by ID from URL params
  useEffect(() => {
    if (users && users.length > 0 && id) {
      const driver = users.find(
        user => user.id === id && user.usertype === 'driver'
      );
      if (driver) {
        setSelectedDriver({
          id: driver.id,
          label: `${driver.firstName} ${driver.lastName} (${driver.email})`,
          email: driver.email,
          mobile: driver.mobile,
          userData: driver,
        });
      } else {
        setMessage('Driver not found');
        setMessageType('error');
      }
    }
  }, [users, id]);

  useEffect(() => {
    if (selectedDriver && selectedDriver.userData.driverCustomPricing) {
      const customPricing = selectedDriver.userData.driverCustomPricing;

      // Merge existing pricing with vehicle defaults for any missing fields
      const vehiclePricing = {};
      if (cartypes && cartypes.cars) {
        cartypes.cars.forEach(carType => {
          const existingVehicleConfig =
            customPricing.vehiclePricing?.[carType.id] || {};
          vehiclePricing[carType.id] = {
            enabled: existingVehicleConfig.enabled || false,
            ratePerKm:
              existingVehicleConfig.ratePerKm !== undefined
                ? existingVehicleConfig.ratePerKm
                : parseFloat(carType.rate_per_unit_distance),
            baseFare:
              existingVehicleConfig.baseFare !== undefined
                ? existingVehicleConfig.baseFare
                : parseFloat(carType.base_fare),
            minFare:
              existingVehicleConfig.minFare !== undefined
                ? existingVehicleConfig.minFare
                : parseFloat(carType.min_fare),
            convenienceFees:
              existingVehicleConfig.convenienceFees !== undefined
                ? existingVehicleConfig.convenienceFees
                : parseFloat(carType.convenience_fees),
            convenienceFeeType:
              existingVehicleConfig.convenienceFeeType ||
              carType.convenience_fee_type ||
              'percentage',
            fleetAdminFee:
              existingVehicleConfig.fleetAdminFee !== undefined
                ? existingVehicleConfig.fleetAdminFee
                : parseFloat(carType.fleet_admin_fee),
          };
        });
      }

      setPricingConfig({
        enabled: customPricing.enabled || false,
        validUntil: customPricing.validUntil || '',
        notes: customPricing.notes || '',
        vehiclePricing,
      });
    } else {
      // Reset to default when no driver selected or no custom pricing
      if (cartypes && cartypes.cars) {
        const vehiclePricing = {};
        cartypes.cars.forEach(carType => {
          vehiclePricing[carType.id] = {
            enabled: false,
            ratePerKm: parseFloat(carType.rate_per_unit_distance),
            baseFare: parseFloat(carType.base_fare),
            minFare: parseFloat(carType.min_fare),
            convenienceFees: parseFloat(carType.convenience_fees),
            convenienceFeeType: carType.convenience_fee_type || 'percentage',
            fleetAdminFee: parseFloat(carType.fleet_admin_fee),
          };
        });
        setPricingConfig({
          enabled: false,
          validUntil: '',
          notes: '',
          vehiclePricing,
        });
      }
    }
  }, [selectedDriver, cartypes]);

  const handlePricingChange = (field, value) => {
    setPricingConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVehiclePricingChange = (vehicleId, field, value) => {
    setPricingConfig(prev => ({
      ...prev,
      vehiclePricing: {
        ...prev.vehiclePricing,
        [vehicleId]: {
          ...prev.vehiclePricing[vehicleId],
          [field]: value,
        },
      },
    }));
  };

  const resetVehiclePricing = vehicleId => {
    const carType = cartypes.cars.find(car => car.id === vehicleId);
    if (carType) {
      handleVehiclePricingChange(vehicleId, 'enabled', false);
      handleVehiclePricingChange(
        vehicleId,
        'ratePerKm',
        parseFloat(carType.rate_per_unit_distance)
      );
      handleVehiclePricingChange(
        vehicleId,
        'baseFare',
        parseFloat(carType.base_fare)
      );
      handleVehiclePricingChange(
        vehicleId,
        'minFare',
        parseFloat(carType.min_fare)
      );
      handleVehiclePricingChange(
        vehicleId,
        'convenienceFees',
        parseFloat(carType.convenience_fees)
      );
      handleVehiclePricingChange(
        vehicleId,
        'convenienceFeeType',
        carType.convenience_fee_type || 'percentage'
      );
      handleVehiclePricingChange(
        vehicleId,
        'fleetAdminFee',
        parseFloat(carType.fleet_admin_fee)
      );
    }
  };

  const handleSave = async () => {
    if (!selectedDriver) {
      setMessage('Please select a driver first');
      setMessageType('error');
      return;
    }

    setIsSaving(true);
    setMessage('');

    try {
      // Only send the driverCustomPricing data to avoid overwriting other user data
      const updateData = {
        driverCustomPricing: {
          ...pricingConfig,
          lastUpdated: new Date().toISOString(),
          updatedBy: 'admin', // You can get this from auth context
        },
      };

      // Dispatch the action and wait for it to complete
      dispatch(api.editUser(selectedDriver.id, updateData));

      // Give it a moment to process
      await new Promise(resolve => setTimeout(resolve, 1000));

      setMessage('Driver custom pricing updated successfully!');
      setMessageType('success');

      // Refresh user data to show updated pricing
      dispatch(api.fetchUsers());
    } catch (error) {
      console.error('Error updating pricing:', error);
      setMessage('Error updating pricing: ' + error.message);
      setMessageType('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemovePricing = async () => {
    if (!selectedDriver) return;

    setIsSaving(true);
    setMessage('');

    try {
      const updateData = {
        driverCustomPricing: null,
      };

      // Dispatch the action
      dispatch(api.editUser(selectedDriver.id, updateData));

      // Give it a moment to process
      await new Promise(resolve => setTimeout(resolve, 1000));

      setMessage('Driver custom pricing removed successfully!');
      setMessageType('success');

      // Reset form
      if (cartypes && cartypes.cars) {
        const vehiclePricing = {};
        cartypes.cars.forEach(carType => {
          vehiclePricing[carType.id] = {
            enabled: false,
            ratePerKm: parseFloat(carType.rate_per_unit_distance),
            baseFare: parseFloat(carType.base_fare),
            minFare: parseFloat(carType.min_fare),
            convenienceFees: parseFloat(carType.convenience_fees),
            convenienceFeeType: carType.convenience_fee_type || 'percentage',
            fleetAdminFee: parseFloat(carType.fleet_admin_fee),
          };
        });
        setPricingConfig({
          enabled: false,
          validUntil: '',
          notes: '',
          vehiclePricing,
        });
      }

      // Refresh user data
      dispatch(api.fetchUsers());
    } catch (error) {
      console.error('Error removing pricing:', error);
      setMessage('Error removing pricing: ' + error.message);
      setMessageType('error');
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading while users are being fetched
  if (!users || users.length === 0) {
    return (
      <div style={{ padding: '20px' }}>
        <GoBackButton isRTL={isRTL} />
        <CircularLoading />
      </div>
    );
  }

  if (loading || isSaving) {
    return <CircularLoading />;
  }

  return (
    <div style={{ padding: '20px' }}>
      <GoBackButton isRTL={isRTL} />

      {message && (
        <Alert severity={messageType} style={{ marginBottom: '16px' }}>
          {message}
        </Alert>
      )}

      {selectedDriver && (
        <Card className={classes.card}>
          <CardHeader title="Driver Information" />
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {selectedDriver.label}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Email: {selectedDriver.email}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Mobile: {selectedDriver.mobile}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Vehicle Type: {selectedDriver.userData.carType || 'Not assigned'}
            </Typography>

            {selectedDriver.userData.driverCustomPricing?.enabled && (
              <>
                <Typography variant="body2" color="textSecondary">
                  Last Updated:{' '}
                  {selectedDriver.userData.driverCustomPricing.lastUpdated
                    ? new Date(
                        selectedDriver.userData.driverCustomPricing.lastUpdated
                      ).toLocaleDateString()
                    : 'Unknown'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Updated By:{' '}
                  {selectedDriver.userData.driverCustomPricing.updatedBy ||
                    'Unknown'}
                </Typography>
                {selectedDriver.userData.driverCustomPricing.validUntil && (
                  <Typography variant="body2" color="textSecondary">
                    Valid Until:{' '}
                    {new Date(
                      selectedDriver.userData.driverCustomPricing.validUntil
                    ).toLocaleDateString()}
                  </Typography>
                )}
                {selectedDriver.userData.driverCustomPricing.notes && (
                  <Typography variant="body2" color="textSecondary">
                    Notes: {selectedDriver.userData.driverCustomPricing.notes}
                  </Typography>
                )}

                {/* Show active vehicle pricing count */}
                <Typography variant="body2" color="primary">
                  Active Vehicle Pricing:{' '}
                  {
                    Object.values(
                      selectedDriver.userData.driverCustomPricing
                        .vehiclePricing || {}
                    ).filter(vehicle => vehicle.enabled).length
                  }{' '}
                  / {cartypes?.cars?.length || 0} vehicles
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {selectedDriver && (
        <Card className={classes.card}>
          <CardHeader title="Driver Commission Configuration" />
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={pricingConfig.enabled}
                  onChange={e =>
                    handlePricingChange('enabled', e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Enable Custom Driver Commission"
              className={classes.formField}
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Vehicle Commission
                </Typography>

                {cartypes &&
                  cartypes.cars &&
                  cartypes.cars.map(carType => (
                    <Accordion
                      key={carType.id}
                      className={classes.vehicleAccordion}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className={classes.vehicleHeader}>
                          <img
                            src={carType.image}
                            alt={carType.name}
                            className={classes.vehicleImage}
                          />
                          <div>
                            <Typography variant="h6">{carType.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {pricingConfig.vehiclePricing[carType.id]?.enabled
                                ? 'Custom pricing enabled'
                                : 'Using standard pricing'}
                            </Typography>
                          </div>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div style={{ width: '100%' }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            marginBottom={2}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    pricingConfig.vehiclePricing[carType.id]
                                      ?.enabled || false
                                  }
                                  onChange={e =>
                                    handleVehiclePricingChange(
                                      carType.id,
                                      'enabled',
                                      e.target.checked
                                    )
                                  }
                                  color="primary"
                                />
                              }
                              label="Enable custom commission for this vehicle"
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              className={classes.resetButton}
                              onClick={() => resetVehiclePricing(carType.id)}
                            >
                              Reset to Standard
                            </Button>
                            {selectedDriver?.userData?.customPricing
                              ?.enabled && (
                              <Button
                                variant="contained"
                                size="small"
                                color="warning"
                                style={{ marginLeft: '8px' }}
                                onClick={() => {
                                  // Enable driver pricing to override customer pricing
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'enabled',
                                    true
                                  );
                                }}
                              >
                                Override Customer Pricing
                              </Button>
                            )}
                          </Box>

                          <Grid
                            container
                            spacing={2}
                            className={classes.vehiclePricingGrid}
                          >
                            <Grid item xs={6}>
                              <TextField
                                label="Rate per KM"
                                type="number"
                                inputProps={{ step: 0.1, min: 0 }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.ratePerKm || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'ratePerKm',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={`Standard: KSH ${carType.rate_per_unit_distance}`}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Base Fare"
                                type="number"
                                inputProps={{ step: 0.1, min: 0 }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.baseFare || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'baseFare',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={`Standard: KSH ${carType.base_fare}`}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Minimum Fare"
                                type="number"
                                inputProps={{ step: 0.1, min: 0 }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.minFare || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'minFare',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={`Standard: KSH ${carType.min_fare}`}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth>
                                <InputLabel
                                  id={`commission-type-label-${carType.id}`}
                                >
                                  Commission Type
                                </InputLabel>
                                <Select
                                  labelId={`commission-type-label-${carType.id}`}
                                  id={`commission-type-select-${carType.id}`}
                                  value={
                                    pricingConfig.vehiclePricing[carType.id]
                                      ?.convenienceFeeType || 'percentage'
                                  }
                                  label="Commission Type"
                                  onChange={e =>
                                    handleVehiclePricingChange(
                                      carType.id,
                                      'convenienceFeeType',
                                      e.target.value
                                    )
                                  }
                                  disabled={
                                    !pricingConfig.vehiclePricing[carType.id]
                                      ?.enabled
                                  }
                                >
                                  <MenuItem value="percentage">
                                    Percentage (%)
                                  </MenuItem>
                                  <MenuItem value="flat">Flat Amount</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.convenienceFeeType === 'flat'
                                    ? 'Platform Commission (Flat)'
                                    : 'Platform Commission (%)'
                                }
                                type="number"
                                inputProps={{
                                  step: 0.1,
                                  min: 0,
                                  max:
                                    pricingConfig.vehiclePricing[carType.id]
                                      ?.convenienceFeeType === 'flat'
                                      ? undefined
                                      : 100,
                                }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.convenienceFees || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'convenienceFees',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.convenienceFeeType === 'flat'
                                    ? `Standard: KSH ${carType.convenience_fees}`
                                    : `Standard: ${carType.convenience_fees}%`
                                }
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Fleet Admin Commission (%)"
                                type="number"
                                inputProps={{ step: 0.1, min: 0, max: 100 }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.fleetAdminFee || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'fleetAdminFee',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={`Standard: ${carType.fleet_admin_fee}%`}
                              />
                            </Grid>
                          </Grid>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Special Settings
                </Typography>

                <TextField
                  label="Valid Until"
                  type="date"
                  value={pricingConfig.validUntil}
                  onChange={e =>
                    handlePricingChange('validUntil', e.target.value)
                  }
                  fullWidth
                  className={classes.formField}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave empty for no expiry"
                />

                <TextField
                  label="Notes"
                  multiline
                  rows={4}
                  value={pricingConfig.notes}
                  onChange={e => handlePricingChange('notes', e.target.value)}
                  fullWidth
                  className={classes.formField}
                  helperText="Internal notes about this commission configuration"
                />

                <Button
                  variant="contained"
                  fullWidth
                  className={classes.saveButton}
                  onClick={handleSave}
                  disabled={isSaving}
                  style={{ marginTop: '16px' }}
                >
                  {isSaving ? 'Saving...' : 'Save Driver Commission'}
                </Button>

                {selectedDriver?.userData?.driverCustomPricing?.enabled && (
                  <Button
                    variant="outlined"
                    fullWidth
                    color="error"
                    onClick={handleRemovePricing}
                    disabled={isSaving}
                    style={{ marginTop: '8px' }}
                  >
                    {isSaving ? 'Removing...' : 'Remove Custom Commission'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Pricing Preview Table */}
      {selectedDriver &&
        cartypes &&
        cartypes.cars &&
        cartypes.cars.length > 0 && (
          <Card className={classes.previewTable}>
            <CardHeader title="Pricing Preview - How Commission Rates Change for Each Vehicle Type" />
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCell}>
                        Vehicle Type
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Rate per KM
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Base Fare
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Minimum Fare
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Platform Commission
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Fleet Admin Commission
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cartypes.cars.map(carType => {
                      const preview = calculatePricingPreview(carType);
                      return (
                        <TableRow key={carType.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <img
                                src={carType.image}
                                alt={carType.name}
                                style={{
                                  width: 40,
                                  height: 40,
                                  objectFit: 'contain',
                                }}
                              />
                              <Typography variant="body2" fontWeight="bold">
                                {carType.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.ratePerKm,
                              preview.adjusted.ratePerKm,
                              preview.changes.ratePerKm,
                              'KSH ',
                              '',
                              preview.hasCustomPricing
                            )}
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.baseFare,
                              preview.adjusted.baseFare,
                              preview.changes.baseFare,
                              'KSH ',
                              '',
                              preview.hasCustomPricing
                            )}
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.minFare,
                              preview.adjusted.minFare,
                              preview.changes.minFare,
                              'KSH ',
                              '',
                              preview.hasCustomPricing
                            )}
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.convenienceFees,
                              preview.adjusted.convenienceFees,
                              preview.changes.convenienceFees,
                              carType.convenience_fee_type === 'flat'
                                ? 'KSH '
                                : '',
                              carType.convenience_fee_type === 'percentage'
                                ? '%'
                                : '',
                              preview.hasCustomPricing
                            )}
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.fleetAdminFee,
                              preview.adjusted.fleetAdminFee,
                              preview.changes.fleetAdminFee,
                              '',
                              '%',
                              preview.hasCustomPricing
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {!pricingConfig.enabled && (
                <Box mt={2}>
                  <Alert severity="info">
                    Enable custom pricing above to see how commission rates will
                    change for this driver.
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
    </div>
  );
};

export default DriverCustomPricingManagement;
